import { Component, OnInit, HostListener, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { AuthenticationService } from 'src/app/components/services/auth/authentication.service';
import { ApiService } from 'src/app/shared/service/api.service';
import { Tramite, Queja, Requisito, Consulta } from 'src/app/components/catalogs/catalogs.models';
import { environment } from '@/environments/environment';
import { getHeaders } from '../tramite.service';

@Component({
  selector: 'app-create-tramites',
  templateUrl: './create-tramites.component.html',
  styleUrls: ['./create-tramites.component.scss']
})

export class CreateTramitesComponent implements OnInit {

  public form: FormGroup;
  public submitted = false;
  public isLoading = false;

  // Datos del usuario
  public currentUser: any;

  // Opciones para selectores - siempre inicializados como arrays vacíos
  public modalidades: any[] = [];
  public areas: any[] = [];
  public dependencias: any[] = [];
  public tiposDocumento: any[] = [];
  public tiposSolicitante: any[] = [
    { value: 'Personas físicas', label: 'Personas físicas' },
    { value: 'Personas morales', label: 'Personas morales' }
  ];
  public tiposModalidades: any[] = [
    { value: 'En línea', label: 'En línea', key: 'isOnline' },
    { value: 'Presencial', label: 'Presencial', key: 'isPresencial' },
    { value: 'Con cita', label: 'Con cita', key: 'isConCita' }
  ];
  public applicantSelected: string[] = [];
  public modalidadesSelected: string[] = [];
  public formasPagoSelected: string[] = [];
  public isSolicitanteDropdownOpen: boolean = false;
  public isModalidadesDropdownOpen: boolean = false;
  public isFormasPagoDropdownOpen: boolean = false;
  public formasPagoOptions: any[] = [
    { value: 'Efectivo', label: 'Efectivo' },
    { value: 'Tarjeta de crédito', label: 'Tarjeta de crédito' },
    { value: 'Tarjeta de débito', label: 'Tarjeta de débito' },
    { value: 'Transferencia', label: 'Transferencia' }
  ];
  
  // Formulario de requisitos
  public requisitosForm: FormGroup;
  public requisitos: any[] = [];

  // Formulario de campos del formulario
  public camposFormularioForm: FormGroup;
  public camposFormulario: any[] = [];
  public tiposDato: any[] = [
    { value: 'Alfanumérico', label: 'Alfanumérico' },
    { value: 'Numérico', label: 'Numérico' },
    { value: 'Lista', label: 'Lista' },
    { value: 'Verdadero/Falso', label: 'Verdadero/Falso' },
    { value: 'Fecha', label: 'Fecha' },
    { value: 'Casilla', label: 'Casilla' }
  ];

  private baseUrl = environment.API_URL;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private http: HttpClient,
    private toastr: ToastrService,
    private authService: AuthenticationService,
    private apiService: ApiService,
    private elementRef: ElementRef
  ) {
    this.buildForm();
    this.buildRequisitosForm();
    this.buildCamposFormularioForm();
    this.loadCurrentUser();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isSolicitanteDropdownOpen) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.isSolicitanteDropdownOpen = false;
      }
    }
    if (this.isModalidadesDropdownOpen) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.isModalidadesDropdownOpen = false;
      }
    }
    if (this.isFormasPagoDropdownOpen) {
      const clickedInside = this.elementRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.isFormasPagoDropdownOpen = false;
      }
    }
  }

  ngOnInit(): void {
    this.loadAreas();
    this.loadDependencias();
    this.loadTiposDocumento();
    this.initializeApplicantSelected();
    this.initializeModalidadesSelected();
    this.initializeFormasPagoSelected();
  }

  private initializeApplicantSelected(): void {
    const applicantValue = this.form.get('applicant')?.value;
    if (Array.isArray(applicantValue)) {
      this.applicantSelected = [...applicantValue];
    } else if (applicantValue && typeof applicantValue === 'string') {
      // Si viene como string separado por comas
      this.applicantSelected = applicantValue.split(',').map(s => s.trim()).filter(s => s);
      this.form.get('applicant')?.setValue(this.applicantSelected);
    }
  }

  toggleSolicitanteDropdown(): void {
    this.isSolicitanteDropdownOpen = !this.isSolicitanteDropdownOpen;
  }

  isApplicantSelected(value: string): boolean {
    return this.applicantSelected.includes(value);
  }

  toggleApplicant(value: string): void {
    const index = this.applicantSelected.indexOf(value);
    if (index > -1) {
      this.applicantSelected.splice(index, 1);
    } else {
      this.applicantSelected.push(value);
    }
    // Actualizar el valor del formulario
    this.form.get('applicant')?.setValue([...this.applicantSelected]);
    this.form.get('applicant')?.markAsTouched();
  }

  private initializeModalidadesSelected(): void {
    const modalidadesValue = this.form.get('modalidades')?.value;
    if (Array.isArray(modalidadesValue)) {
      this.modalidadesSelected = [...modalidadesValue];
    } else {
      // Inicializar desde los campos booleanos existentes
      const selected: string[] = [];
      if (this.form.get('isOnline')?.value) {
        selected.push('En línea');
      }
      if (this.form.get('isPresencial')?.value) {
        selected.push('Presencial');
      }
      if (this.form.get('isConCita')?.value) {
        selected.push('Con cita');
      }
      this.modalidadesSelected = selected;
      this.form.get('modalidades')?.setValue(selected);
    }
  }

  toggleModalidadesDropdown(): void {
    this.isModalidadesDropdownOpen = !this.isModalidadesDropdownOpen;
  }

  isModalidadSelected(value: string): boolean {
    return this.modalidadesSelected.includes(value);
  }

  toggleModalidad(value: string): void {
    const index = this.modalidadesSelected.indexOf(value);
    if (index > -1) {
      this.modalidadesSelected.splice(index, 1);
    } else {
      this.modalidadesSelected.push(value);
    }
    // Actualizar el valor del formulario
    this.form.get('modalidades')?.setValue([...this.modalidadesSelected]);
    this.form.get('modalidades')?.markAsTouched();
    
    // Actualizar también los campos booleanos individuales para compatibilidad
    const modalidad = this.tiposModalidades.find(m => m.value === value);
    if (modalidad) {
      this.form.get(modalidad.key)?.setValue(index === -1);
    }
  }

  private initializeFormasPagoSelected(): void {
    const formasPagoValue = this.form.get('formasPago')?.value;
    if (Array.isArray(formasPagoValue)) {
      this.formasPagoSelected = [...formasPagoValue];
    } else if (formasPagoValue && typeof formasPagoValue === 'string') {
      // Si viene como string separado por comas
      this.formasPagoSelected = formasPagoValue.split(',').map(s => s.trim()).filter(s => s);
      this.form.get('formasPago')?.setValue(this.formasPagoSelected);
    }
  }

  toggleFormasPagoDropdown(): void {
    this.isFormasPagoDropdownOpen = !this.isFormasPagoDropdownOpen;
  }

  isFormaPagoSelected(value: string): boolean {
    return this.formasPagoSelected.includes(value);
  }

  toggleFormaPago(value: string): void {
    const index = this.formasPagoSelected.indexOf(value);
    if (index > -1) {
      this.formasPagoSelected.splice(index, 1);
    } else {
      this.formasPagoSelected.push(value);
    }
    // Actualizar el valor del formulario
    this.form.get('formasPago')?.setValue([...this.formasPagoSelected]);
    this.form.get('formasPago')?.markAsTouched();
  }

  private buildRequisitosForm(): void {
    this.requisitosForm = this.fb.group({
      nombre: ['', [Validators.required]],
      descripcion: ['', [Validators.required]]
    });
  }

  addRequisito(): void {
    if (this.requisitosForm.valid) {
      const nuevoRequisito = {
        id: Date.now(), // ID temporal único
        nombre: this.requisitosForm.get('nombre')?.value,
        descripcion: this.requisitosForm.get('descripcion')?.value
      };
      this.requisitos.push(nuevoRequisito);
      this.requisitosForm.reset();
    } else {
      this.requisitosForm.markAllAsTouched();
    }
  }

  removeRequisito(index: number): void {
    this.requisitos.splice(index, 1);
  }

  private buildCamposFormularioForm(): void {
    this.camposFormularioForm = this.fb.group({
      nombreCampo: ['', [Validators.required]],
      tipoDato: ['', [Validators.required]],
      longitudCampo: [null],
      obligatorio: [false]
    });
  }

  addCampoFormulario(): void {
    if (this.camposFormularioForm.valid) {
      const nuevoCampo = {
        id: Date.now(), // ID temporal único
        nombreCampo: this.camposFormularioForm.get('nombreCampo')?.value,
        tipoDato: this.camposFormularioForm.get('tipoDato')?.value,
        longitudCampo: this.camposFormularioForm.get('longitudCampo')?.value || null,
        obligatorio: this.camposFormularioForm.get('obligatorio')?.value || false
      };
      this.camposFormulario.push(nuevoCampo);
      this.camposFormularioForm.reset({ obligatorio: false });
    } else {
      this.camposFormularioForm.markAllAsTouched();
    }
  }

  removeCampoFormulario(index: number): void {
    this.camposFormulario.splice(index, 1);
  }

  private buildForm(): void {
    this.form = this.fb.group({
      // Sección: Información Básica (FormsCommon)
      name: ['', [Validators.required, Validators.minLength(3)]],
      code: ['', [Validators.required]],
      type: ['', [Validators.required]],
      area: ['', [Validators.required]],
      tieneCosto: [false],
      costo: [null],
      formasPago: [[]],
      
      // Modalidades
      modalidades: [[], []],
      // Campos booleanos individuales (mantenidos para compatibilidad con API)
      isOnline: [false],
      isPresencial: [false],
      isConCita: [false],

      // Solicitante
      applicant: [[], [this.arrayRequiredValidator]],

      // Tiempo de Respuesta
      answerTime: [0, [Validators.required, Validators.min(0)]],
      answerTimeType: ['Días hábiles', [Validators.required]],

      // Vigencia
      validity: [0, [Validators.min(0)]],
      validityType: ['Días naturales'],

      // Sección: Detalles (FormsDetail)
      description: ['', [Validators.required]],
      legalBase: ['', [Validators.required]],
      recipient: [''],
      resolutionCriteria: [''],
      authorizedFormats: [''],
      rightBreachAnswer: [''],
      
      // Oficina
      office: [''],
      location: [''],
      address: [''],
      officePhone: [''],
      extension: [''],
      schedule: [''],

      // Configuración
      isVisible: [true],
      isLimited: [false],
      isRequestable: [true],

      // Sección: Consulta (FormsInquiry)
      attender: ['', [Validators.required]],
      role: [''],
      inquiryPhone: [''],
      inquiryEmail: ['', Validators.email],
      inquiryAddress: [''],
      inquirySchedule: [''],
      
      // Form
      department: ['']
    });
  }

  private loadCurrentUser(): void {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        this.currentUser = JSON.parse(userStr);
      }
    } catch (error) {
      console.error('Error al cargar usuario:', error);
    }
  }

  private loadAreas(): void {
    this.apiService.getCatalogInfo('Areas', 1000, 0)
      .subscribe(
        (data: any) => {
          console.log('Datos de áreas recibidos:', data);
          let areasArray: any[] = [];
          
          if (data && data.list && Array.isArray(data.list)) {
            areasArray = data.list.map((area: any) => ({
              id: area.id,
              Name: area.ValueAlpha || area.Name || area.name || ''
            })).filter((area: any) => area.Name && area.id);
          } else if (Array.isArray(data)) {
            areasArray = data.map((area: any) => ({
              id: area.id,
              Name: area.ValueAlpha || area.Name || area.name || ''
            })).filter((area: any) => area.Name && area.id);
          }
          
          // Asegurar que siempre sea un array válido
          this.areas = Array.isArray(areasArray) ? [...areasArray] : [];
          console.log('Áreas procesadas:', this.areas);
        },
        (error) => {
          console.error('Error al cargar áreas:', error);
          this.sendNotification('Error', 'No se pudieron cargar las áreas', 'error');
          this.areas = [];
        }
      );
  }

  private loadDependencias(): void {
    this.apiService.getCatalogInfo('Departments', 1000, 0)
      .subscribe(
        (data: any) => {
          console.log('Datos de dependencias recibidos:', data);
          let dependenciasArray: any[] = [];
          
          if (data && data.list && Array.isArray(data.list)) {
            dependenciasArray = data.list.map((dep: any) => ({
              id: dep.id,
              Name: dep.ValueAlpha || dep.Name || dep.name || ''
            })).filter((dep: any) => dep.Name && dep.id);
          } else if (Array.isArray(data)) {
            dependenciasArray = data.map((dep: any) => ({
              id: dep.id,
              Name: dep.ValueAlpha || dep.Name || dep.name || ''
            })).filter((dep: any) => dep.Name && dep.id);
          }
          
          // Asegurar que siempre sea un array válido
          this.dependencias = Array.isArray(dependenciasArray) ? [...dependenciasArray] : [];
          console.log('Dependencias procesadas:', this.dependencias);
        },
        (error) => {
          console.error('Error al cargar dependencias:', error);
          this.sendNotification('Error', 'No se pudieron cargar las dependencias', 'error');
          this.dependencias = [];
        }
      );
  }

  private loadTiposDocumento(): void {
    this.apiService.getCatalogInfo('DocumentTypes', 1000, 0)
      .subscribe(
        (data: any) => {
          console.log('Datos de tipos de documento recibidos:', data);
          let tiposDocumentoArray: any[] = [];
          
          if (data && data.list && Array.isArray(data.list)) {
            tiposDocumentoArray = data.list.map((tipo: any) => ({
              id: tipo.id,
              value: tipo.ValueAlpha || tipo.Name || tipo.name || '',
              label: tipo.ValueAlpha || tipo.Name || tipo.name || ''
            })).filter((tipo: any) => tipo.value && tipo.id);
          } else if (data && data.rows && Array.isArray(data.rows)) {
            tiposDocumentoArray = data.rows.map((tipo: any) => ({
              id: tipo.id,
              value: tipo.ValueAlpha || tipo.Name || tipo.name || '',
              label: tipo.ValueAlpha || tipo.Name || tipo.name || ''
            })).filter((tipo: any) => tipo.value && tipo.id);
          } else if (Array.isArray(data)) {
            tiposDocumentoArray = data.map((tipo: any) => ({
              id: tipo.id,
              value: tipo.ValueAlpha || tipo.Name || tipo.name || '',
              label: tipo.ValueAlpha || tipo.Name || tipo.name || ''
            })).filter((tipo: any) => tipo.value && tipo.id);
          }
          
          // Asegurar que siempre sea un array válido
          this.tiposDocumento = Array.isArray(tiposDocumentoArray) ? [...tiposDocumentoArray] : [];
          console.log('Tipos de documento procesados:', this.tiposDocumento.length, 'registros');
        },
        (error) => {
          console.error('Error al cargar tipos de documento:', error);
          this.sendNotification('Error', 'No se pudieron cargar los tipos de documento', 'error');
          // Si el catálogo no existe, mantener valores por defecto como fallback
          this.tiposDocumento = [];
        }
      );
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.sendNotification('Error', 'Por favor, completa todos los campos requeridos', 'error');
      return;
    }

    this.isLoading = true;

    const formData = this.prepareSubmitData();
    
    this.http.post<any>(`${this.baseUrl}/miveracruz/forms`, formData, { headers: getHeaders() })
      .subscribe(
        (response) => {
          this.sendNotification('Éxito', 'Trámite creado exitosamente', 'success');
          setTimeout(() => {
            this.router.navigate(['/catalogs/consulta-catalogo']);
          }, 1500);
        },
        (error) => {
          console.error('Error al crear trámite:', error);
          this.sendNotification('Error', 'No se pudo crear el trámite', 'error');
          this.isLoading = false;
        }
      );
  }

  private prepareSubmitData(): any {
    const formValue = this.form.value;
    const applicantValue = Array.isArray(formValue.applicant) 
      ? formValue.applicant.join(', ') 
      : formValue.applicant;
    
    // Convertir array de modalidades a booleanos
    const modalidadesArray = Array.isArray(formValue.modalidades) ? formValue.modalidades : [];
    const isOnline = modalidadesArray.includes('En línea');
    const isPresencial = modalidadesArray.includes('Presencial');
    const isConCita = modalidadesArray.includes('Con cita');
    
    return {
      Common: {
        Name: formValue.name,
        Code: formValue.code,
        Type: formValue.type,
        IsOnline: isOnline,
        Applicant: applicantValue,
        AnswerTime: formValue.answerTime,
        AnswerTimeType: formValue.answerTimeType,
        Validity: formValue.validity,
        ValidityType: formValue.validityType,
        Cost: formValue.tieneCosto ? (formValue.costo || null) : null,
        PaymentMethods: formValue.tieneCosto 
          ? (Array.isArray(formValue.formasPago) ? formValue.formasPago.join(', ') : (formValue.formasPago || '')) 
          : ''
      },
      Detail: {
        Description: formValue.description,
        LegalBase: formValue.legalBase,
        Recipient: formValue.recipient,
        ResolutionCriteria: formValue.resolutionCriteria,
        AuthorizedFormats: formValue.authorizedFormats,
        RightBreachAnswer: formValue.rightBreachAnswer,
        Office: formValue.office,
        Location: formValue.location,
        OfficePhone: formValue.officePhone,
        Extension: formValue.extension,
        Address: formValue.address,
        Schedule: formValue.schedule,
        IsVisible: formValue.isVisible,
        IsLimited: formValue.isLimited,
        IsRequestable: formValue.isRequestable
      },
      Inquiry: {
        Attender: formValue.attender,
        Role: formValue.role,
        Address: formValue.inquiryAddress,
        Schedule: formValue.inquirySchedule,
        Phone: formValue.inquiryPhone,
        Email: formValue.inquiryEmail,
        Type: 1
      },
      Form: {
        AreaId: formValue.area,
        DepartmentId: formValue.department || null,
        created_by: this.currentUser?.id || ''
      }
    };
  }

  cancel(): void {
    if (confirm('¿Deseas cancelar? Los cambios no guardados se perderán.')) {
      this.router.navigate(['/catalogs/consulta-catalogo']);
    }
  }

  get formControls() {
    return this.form.controls;
  }

  arrayRequiredValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value || !Array.isArray(value) || value.length === 0) {
      return { required: true };
    }
    return null;
  }

  hasError(fieldName: string): boolean {
    const control = this.form.controls[fieldName];
    return control.invalid && (control.dirty || control.touched || this.submitted);
  }

  getErrorMessage(fieldName: string): string {
    const control = this.form.controls[fieldName];
    if (control.hasError('required')) {
      return 'Este campo es requerido';
    }
    if (control.hasError('minlength')) {
      return `Debe tener al menos ${control.errors.minlength.requiredLength} caracteres`;
    }
    if (control.hasError('email')) {
      return 'Email inválido';
    }
    if (control.hasError('min')) {
      return 'El valor debe ser mayor a 0';
    }
    return '';
  }

  sendNotification(title: string = 'Mi Veracruz', message: string, type: string): void {
    this.toastr[type](message, title, { timeOut: 3500 });
  }
}